import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  /**
   * Time each message stays on screen. The theme setting is whole seconds;
   * the 1.1 factor slows the cycle by 10% so the text is easier to read.
   */
  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000 * 1.1;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    // Same vertical roll on every screen size: the outgoing message slides up
    // and out, the incoming one rises in from below. 440ms is 10% slower than
    // the original 400ms.
    const slides = this.refs.slides ?? [];
    const outgoing = slides.find((s, i) => s.getAttribute('aria-hidden') === 'false' && i !== relativeIndex);
    const incoming = slides[relativeIndex];

    if (outgoing) {
      outgoing.style.transition = 'transform 440ms cubic-bezier(0.22,1,0.36,1), opacity 440ms';
      outgoing.style.transform = 'translateY(-100%)';
      outgoing.style.opacity = '0';
      setTimeout(() => {
        outgoing.setAttribute('aria-hidden', 'true');
        outgoing.style.transform = '';
        outgoing.style.opacity = '';
        outgoing.style.transition = '';
      }, 440);
    }

    if (incoming) {
      incoming.style.transform = 'translateY(100%)';
      incoming.style.opacity = '0';
      incoming.style.transition = 'none';
      incoming.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          incoming.style.transition = 'transform 440ms cubic-bezier(0.22,1,0.36,1), opacity 440ms';
          incoming.style.transform = 'translateY(0)';
          incoming.style.opacity = '1';
        });
      });
    }
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
